import useSWRMutation from "swr/mutation";
import { mutate as globalMutate } from "swr";
import { useCosmosClient } from "@/services/cosmos";
import { type CheckoutSession } from "@/types";
import { CksCheckoutStateSchema } from "@/services/cosmos/schema/checkout-service";
import { logError, logInfo } from "@/services/logging/logger";
import {
  handleHttpError,
  type ApiError,
} from "@/lib/http-error-recovery";
import type { SelectPaymentOptionArgs } from "../types";

/**
 * Mutation hook for selecting a payment option.
 *
 * Behavioral contract (must not change):
 * - POSTs via cosmosClient.selectPaymentOption
 * - On error: attempts smart recovery via handleHttpError
 * - If recovery succeeds: returns fresh session (UI navigates based on actual state)
 * - If recovery fails: updates SWR cache with error state, throws enhanced error
 * - populateCache writes returned session directly to SWR "session" key
 * - shouldRetryOnError: false, revalidate: false
 */
export function useSelectPaymentOption() {
  const cosmosClient = useCosmosClient();

  return useSWRMutation(
    "session",
    async (
      _: string,
      { arg }: { arg: SelectPaymentOptionArgs },
    ): Promise<CheckoutSession> => {
      const currentSession = await globalMutate(
        "session",
        (current: CheckoutSession | undefined) => current,
        { revalidate: false },
      );

      try {
        const data = await cosmosClient.selectPaymentOption(
          arg.paymentOptionOfferId,
          arg.tenure,
        );
        return CksCheckoutStateSchema.parse(data);
      } catch (err: unknown) {
        logError(
          "checkout-session",
          "Error selecting payment option",
          err instanceof Error ? err : new Error(String(err)),
          {
            error: err,
            paymentOptionOfferId: arg.paymentOptionOfferId,
            tenure: arg.tenure,
          },
        );

        const recoveryResult = await handleHttpError(err as ApiError, {
          refetchSession: async () => {
            const freshData = await cosmosClient.getCheckoutSessionV3();
            return CksCheckoutStateSchema.parse(freshData);
          },
          currentSession: currentSession ?? undefined,
          endpoint: "select-payment-option",
          httpMethod: "POST",
          requestContext: {
            paymentOptionOfferId: arg.paymentOptionOfferId,
            tenure: arg.tenure,
          },
        });

        if (recoveryResult.recovered) {
          logInfo(
            "checkout-session",
            "Recovered from select-payment-option error",
            {
              originalError: recoveryResult.originalError.response?.status,
              recoveredSessionStatus: recoveryResult.session.status,
            },
          );
          return recoveryResult.session;
        }

        await globalMutate("session", recoveryResult.session, {
          revalidate: false,
        });

        const enhancedError = Object.assign(err as Error, {
          recoveryAttempted: true,
          recoveryResult,
        });
        throw enhancedError;
      }
    },
    {
      populateCache: (newVal: CheckoutSession) => newVal,
      onError: (error: unknown) => {
        const enhanced = error as { recoveryAttempted?: boolean };
        if (!enhanced.recoveryAttempted) {
          logError(
            "checkout-session",
            "Error selecting payment option (onError)",
            error instanceof Error ? error : new Error(String(error)),
            { error },
          );
        }
      },
      revalidate: false,
    },
  );
}
